import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Editorial image wrapper.
 *
 * - Renders a `<picture>` with a WebP source and JPG/PNG fallback.
 * - Defaults to `loading="lazy"` and `decoding="async"`.
 * - Forces `width`/`height` to prevent layout shift (CLS).
 * - Falls back to a cream-tinted placeholder block on load failure.
 * - Optional `cdnProxy` routes external URLs through Netlify Image CDN
 *   for on-the-fly AVIF / WebP + edge cache (LCP win on article heros).
 *
 * Pass a `src` ending in `.jpg`/`.jpeg`/`.png` and we'll auto-derive the
 * matching `.webp` URL. Override with `webpSrc` if your CDN uses a
 * different convention.
 *
 * @param {object} props
 * @param {string} props.src              Fallback (jpg/png) URL.
 * @param {string} props.alt              Accessible description (required).
 * @param {number} props.width            Intrinsic width in CSS px.
 * @param {number} props.height           Intrinsic height in CSS px.
 * @param {string} [props.webpSrc]        Explicit WebP URL.
 * @param {string} [props.sizes]          Responsive sizes attribute.
 * @param {string} [props.srcSet]         Responsive srcSet for fallback.
 * @param {string} [props.webpSrcSet]     Responsive srcSet for WebP source.
 * @param {boolean}[props.priority=false] When true, eager-loads + high fetchpriority.
 * @param {boolean}[props.cdnProxy=false] Route external https URLs through
 *                                         `/.netlify/images?url=...` for AVIF
 *                                         + edge cache. No-op on localhost.
 * @param {number} [props.cdnQuality=80]  Quality for the CDN-rewritten image.
 * @param {string} [props.className]
 *
 * @example
 *   <OptimizedImage
 *     src="/articles/cover.jpg"
 *     alt="Skyline of Panama City at dusk"
 *     width={1200}
 *     height={675}
 *     sizes="(max-width: 768px) 100vw, 800px"
 *     cdnProxy
 *     priority
 *   />
 */

// Rewrite an external https URL through Netlify Image CDN. Returns the
// original URL on localhost (CDN endpoint isn't available) or if the URL
// isn't a remote https resource.
function rewriteThroughNetlifyCdn(src, { width, quality = 80, fit = 'cover' } = {}) {
  if (!src || !/^https?:\/\//.test(src)) return src;
  const isLocal = typeof window !== 'undefined'
    && (window.location.hostname === 'localhost' || window.location.hostname.startsWith('127.'));
  if (isLocal) return src;
  const params = new URLSearchParams();
  params.set('url', src);
  if (width) params.set('w', String(width));
  if (quality) params.set('q', String(quality));
  if (fit) params.set('fit', fit);
  return `/.netlify/images?${params.toString()}`;
}

export default function OptimizedImage({
  src,
  alt,
  width,
  height,
  webpSrc,
  sizes,
  srcSet,
  webpSrcSet,
  priority = false,
  cdnProxy = false,
  cdnQuality = 80,
  className = '',
  ...rest
}) {
  const { t } = useTranslation('components');
  const [failed, setFailed] = useState(false);
  // 2026-05-16 : si la CDN-proxied URL échoue (typique = domaine pas
  // dans `remote_images` du netlify.toml), on retombe sur l'URL
  // d'origine au lieu d'afficher direct le placeholder cassé. La
  // plupart du temps l'image charge nativement en JPG (-AVIF mais ok).
  const [cdnFailed, setCdnFailed] = useState(false);

  // Auto-derive WebP URL when not provided AND src is local. For remote
  // hosts (Wikimedia, Unsplash, Pexels) the .webp URL almost never exists
  // — the <picture> source would 404, and some browsers don't gracefully
  // fall back to the <img> when source URLs error. So we only derive WebP
  // for same-origin paths where we control the build pipeline.
  //
  // 2026-05-17 — Was previously deriving for any jpg/png which produced
  // broken WebP URLs on Wikimedia ("Image unavailable" on Home cards).
  const isRemote = src && /^https?:\/\//.test(src);
  const derivedWebp =
    webpSrc ?? (!isRemote && src && /\.(jpe?g|png)$/i.test(src)
      ? src.replace(/\.(jpe?g|png)$/i, '.webp')
      : null);

  // Optionally route the fallback through Netlify Image CDN. The CDN
  // auto-negotiates AVIF/WebP based on Accept headers — so we DON'T emit
  // a <source type="image/webp"> when going through the CDN. The bare
  // <img> with the CDN URL already gets AVIF/WebP delivered when supported.
  // If the CDN fails (cdnFailed = true, e.g. host not in remote_images
  // allowlist) we fall back to the origin URL.
  const cdnSrc = cdnProxy && !cdnFailed
    ? rewriteThroughNetlifyCdn(src, { width, quality: cdnQuality })
    : src;
  // Only emit a separate WebP <source> when NOT using the CDN. With the
  // CDN, Accept-based negotiation already returns the best format.
  const cdnWebp = cdnProxy ? null : derivedWebp;

  if (failed || !src) {
    return (
      <div
        role="img"
        aria-label={alt || t('media.image.loadFailed')}
        className={`flex items-center justify-center bg-cream-100 text-xs text-gray-700 ${className}`}
        style={{ aspectRatio: width && height ? `${width} / ${height}` : undefined }}
      >
        {t('media.image.loadFailed')}
      </div>
    );
  }

  return (
    <picture>
      {cdnWebp && (
        <source type="image/webp" srcSet={webpSrcSet ?? cdnWebp} sizes={sizes} />
      )}
      <img
        src={cdnSrc}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? 'eager' : 'lazy'}
        // camelCase `fetchPriority` is only recognized starting in React 19;
        // on React 18.3 it logs a "React does not recognize the fetchPriority
        // prop" warning on every render. Use the lowercase `fetchpriority`,
        // which React 18 passes straight through to the DOM unchanged.
        fetchpriority={priority ? 'high' : 'auto'}
        // For priority/LCP images, `decoding="sync"` reduces TTI a few ms
        // (the browser blocks paint until decode finishes — ideal here).
        // For lazy images we keep async to avoid main-thread blocking.
        decoding={priority ? 'sync' : 'async'}
        onError={() => {
          // Premier échec : si on est en mode CDN-proxied, on retente
          // avec l'URL d'origine (le composant re-render avec cdnSrc =
          // src). Deuxième échec : on capitule et on affiche le
          // placeholder.
          if (cdnProxy && !cdnFailed) {
            setCdnFailed(true);
            if (typeof window !== 'undefined' && !window.__ip_cdnFailLogged) {
              window.__ip_cdnFailLogged = new Set();
            }
            if (typeof window !== 'undefined' && !window.__ip_cdnFailLogged.has(src)) {
              window.__ip_cdnFailLogged.add(src);
              // eslint-disable-next-line no-console
              console.warn('[OptimizedImage] CDN proxy failed, fallback to origin :', src);
            }
            return;
          }
          setFailed(true);
        }}
        className={className}
        {...rest}
      />
    </picture>
  );
}
